import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/utils/api";
import SEOHead, { generateBreadcrumbSchema, generateWebPageSchema } from "@/components/SEOHead";
import HelmetSEO from "@/components/HelmetSEO";
import { ChevronRight } from "lucide-react";

const SeoAdPage = () => {
  const { city, category, slug } = useParams();
  const navigate = useNavigate();

  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cityName, setCityName] = useState(city);
  const [categoryName, setCategoryName] = useState(category);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [citiesRes, categoriesRes] = await Promise.all([
          api.get("/cities"),
          api.get("/categories"),
        ]);

        if (city !== "india") {
          const c = citiesRes.data.find((x) => x.slug === city);
          if (c?.name) setCityName(c.name);
        } else {
          setCityName("India");
        }

        const cat = categoriesRes.data.find((x) => x.slug === category);
        if (cat?.name) setCategoryName(cat.name);

        const res = await api.get(
          `/seo/${encodeURIComponent(city)}/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`
        );
        setAd(res.data);
      } catch (e) {
        setAd(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [city, category, slug]);

  const canonicalUrl = useMemo(() => {
    return `https://uplino.in/${city}/${category}/${slug}`;
  }, [city, category, slug]);

  const isExpired = useMemo(() => {
    if (!ad) return false;
    if (ad.status && ad.status !== "approved") return true;
    if (ad.expires_at && typeof ad.expires_at === "string") {
      return new Date(ad.expires_at).getTime() < Date.now();
    }
    return false;
  }, [ad]);

  const title = useMemo(() => {
    if (!ad) return "Ad not found | uplino.in";
    const cityLabel = city === "india" ? "India" : cityName;
    return `${ad.title} | ${categoryName} in ${cityLabel} | uplino.in`;
  }, [ad, categoryName, city, cityName]);

  const description = useMemo(() => {
    if (!ad) return "";
    const base = (ad.description || "").replace(/\s+/g, " ").trim();
    return base.length > 155 ? `${base.slice(0, 152)}...` : base;
  }, [ad]);

  const schema = useMemo(() => {
    const cityLabel = city === "india" ? "India" : cityName;
    return {
      "@context": "https://schema.org",
      "@graph": [
        generateWebPageSchema(title, description, canonicalUrl),
        generateBreadcrumbSchema([
          { name: "Home", url: "https://uplino.in" },
          { name: cityLabel, url: `https://uplino.in/${city}` },
          { name: categoryName, url: `https://uplino.in/${city}/${category}` },
          { name: ad?.title || "Ad", url: canonicalUrl },
        ]),
      ],
    };
  }, [ad?.title, canonicalUrl, category, categoryName, city, cityName, description, title]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <HelmetSEO title="Ad not found | uplino.in" description="" canonical={canonicalUrl} noIndex />
        <SEOHead title="Ad not found | uplino.in" description="" canonical={canonicalUrl} noIndex />
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16">
          <Card className="p-8 bg-white/5 border-white/10 text-center">
            <div className="text-white text-xl mb-2">Ad not found</div>
            <Button onClick={() => navigate("/")} className="mt-4">
              Go Home
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const cityLabel = city === "india" ? "India" : cityName;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <HelmetSEO
        title={title}
        description={description}
        canonical={canonicalUrl}
        noIndex={isExpired}
      />
      <SEOHead
        title={title}
        description={description}
        canonical={canonicalUrl}
        schema={schema}
        noIndex={isExpired}
      />
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <nav aria-label="Breadcrumb" className="mb-4 sm:mb-6">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-400 hover:text-white">
                Home
              </Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li>
              <Link to={`/${city}/${category}`} className="text-gray-400 hover:text-white">
                {categoryName} in {cityLabel}
              </Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li>
              <span className="text-white font-medium">{ad.title}</span>
            </li>
          </ol>
        </nav>

        {isExpired && (
          <Card className="p-4 mb-6 bg-yellow-500/10 border-yellow-500/20 text-yellow-200">
            This ad is expired or inactive. It remains accessible, but will not be indexed.
          </Card>
        )}

        <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border-white/10 overflow-hidden shadow-2xl">
          <div className="p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">{ad.title}</h1>
            <div className="text-gray-400 mb-6 text-sm">
              {categoryName} • {cityLabel}
            </div>
            <div className="bg-white/5 rounded-xl p-4 sm:p-6">
              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{ad.description}</p>
            </div>

            <div className="mt-8">
              <Button onClick={() => navigate(`/ad/${ad.id}`)} className="bg-blue-600 hover:bg-blue-700">
                View full details / contact
              </Button>
            </div>
          </div>
        </Card>

        <section className="mt-12 sm:mt-16 mb-8 sm:mb-12">
          <Card className="p-6 sm:p-8 bg-white/5 backdrop-blur-sm border-white/10">
            <details>
              <summary className="cursor-pointer text-white font-semibold">More about {categoryName} in {cityLabel}</summary>
              <div className="mt-3 text-gray-400 text-sm sm:text-base">
                Explore verified listings in {cityLabel}. Always follow local laws and stay safe.
              </div>
            </details>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SeoAdPage;
